import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  schema
}: SEOProps) => {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const fullUrl = canonical || `${siteUrl}${location.pathname}`;

  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    
    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', fullUrl, true);
    
    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
    }

    // Twitter Card tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    
    if (ogImage) {
      updateMetaTag('twitter:image', ogImage);
    }

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullUrl);

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Add or update Schema.org JSON-LD
    if (schema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      
      scriptTag.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      const schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [title, description, keywords, fullUrl, ogType, ogImage, schema]);
};

export const generateLocalBusinessSchema = (data: {
  name: string;
  description: string;
  address: string;
  telephone?: string;
  priceRange?: string;
  image?: string;
  serviceArea?: string[];
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${siteUrl}/#business`,
    name: data.name,
    description: data.description,
    image: data.image || `${siteUrl}/vite.svg`,
    address: {
      '@type': 'PostalAddress',
      addressLocality: data.address,
      addressRegion: 'Western Cape',
      addressCountry: 'ZA'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '-33.8568',
      longitude: '18.5653'
    },
    url: siteUrl,
    telephone: data.telephone || '',
    priceRange: data.priceRange || '$$',
    areaServed: data.serviceArea?.map(area => ({
      '@type': 'City',
      name: area
    })) || []
  };
};

export const generateWebPageSchema = (data: {
  name: string;
  description: string;
  url: string;
}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${data.url}#webpage`,
    url: data.url,
    name: data.name,
    description: data.description,
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website`,
      url: import.meta.env.VITE_SITE_URL || 'https://example.com',
      name: 'PAINTERRR'
    }
  };
};

export const generateServiceSchema = (data: {
  name: string;
  description: string;
  provider: string;
  areaServed: string | string[];
  serviceType: string | string[];
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  // Convert areaServed to array if it's a string
  const areasArray = Array.isArray(data.areaServed) 
    ? data.areaServed 
    : [data.areaServed];
  
  // Convert serviceType to array if it's a string
  const serviceTypesArray = Array.isArray(data.serviceType)
    ? data.serviceType
    : [data.serviceType];
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: serviceTypesArray,
    provider: {
      '@type': 'LocalBusiness',
      name: data.provider,
      address: {
        '@type': 'PostalAddress',
        addressLocality: areasArray[0],
        addressRegion: 'Western Cape',
        addressCountry: 'ZA'
      }
    },
    areaServed: areasArray.map(area => ({
      '@type': 'City',
      name: area
    })),
    description: data.description,
    url: siteUrl
  };
};
